import { useCallback, useMemo, useRef, useState } from 'react';
import { type ColumnDef } from '@tanstack/react-table';
import { Alert, Badge, Button, InlineEditRowActions, Modal, Table, TextLink, Tooltip } from '@/shared/components';
import {
    ConfigValueEditCell,
    type ConfigEditErrorSink,
    type ConfigSettingLike,
    coerceConfigValue,
    dataTypeOf,
    formatConfigValue,
    seedConfigDraft,
} from '@/shared/components/form/config-value';
import { SquarePenIcon, Trash2Icon } from 'lucide-react';
import { PermissionGuard, type PermissionCheck } from '@/features/auth';
import { useInlineRowEdit } from '@/shared/hooks/useInlineRowEdit';
import { useLatestRef } from '@/shared/hooks/useLatestRef';
import { apiErrorMessage } from '@/shared/utils/apiErrorMessage';
import type { ConfigSettingTableMutations } from './types';

// Adapts a callback-style `mutate(vars, { onSuccess, onError })` mutation into
// the promise `useInlineRowEdit#commit` awaits.
function mutateAsPromise<TVars>(
    mutate: (vars: TVars, options: { onSuccess: () => void; onError: (error: unknown) => void }) => void,
    vars: TVars,
): Promise<void> {
    return new Promise((resolve, reject) => {
        mutate(vars, { onSuccess: resolve, onError: reject });
    });
}

export const UNCATEGORISED = 'Uncategorised';

interface ConfigSettingsDataTableProps<TSetting extends ConfigSettingLike> {
    data: TSetting[];
    mutations: ConfigSettingTableMutations;
    permissions: { edit: PermissionCheck; delete: PermissionCheck };
    entityNoun: string; // 'dealer' | 'product' — modal copy + derived aria id
    groupLabel: (s: TSetting) => string;
    groupOrder?: string[]; // fixed sort; absent = alphabetical, Uncategorised last
    repeatableKeys?: string[]; // rows addressed by id even when their key is unique
    readOnly?: boolean; // hides row actions (smr retired products)
}

export function ConfigSettingsDataTable<TSetting extends ConfigSettingLike>({
    data,
    mutations,
    permissions,
    entityNoun,
    groupLabel,
    groupOrder,
    repeatableKeys,
    readOnly = false,
}: ConfigSettingsDataTableProps<TSetting>) {
    const inlineEdit = useInlineRowEdit<string>();
    const [deleteError, setDeleteError] = useState<string | null>(null);
    const [pendingDelete, setPendingDelete] = useState<TSetting | null>(null);
    // Per-keystroke draft kept out of the column memo deps so typing never rebuilds cell identities.
    const draftRef = useRef('');
    // The open editor registers its error setter here, so a rejected save re-renders that cell alone.
    const editErrorRef: ConfigEditErrorSink = useRef<((error: string | null) => void) | null>(null);
    // Bind the stable `mutate` identities, not the per-render mutation result objects.
    const { mutate: updateMutate } = mutations.updateMutation;
    const { mutate: updateByIdMutate } = mutations.updateByIdMutation;

    // Address by `id` when the key is declared repeatable or duplicated in the list; otherwise by `key`.
    const repeatableKeySet = useMemo(() => new Set(repeatableKeys), [repeatableKeys]);
    const keyCounts = useMemo(() => data.reduce<Record<string, number>>((acc, s) => {
        acc[s.key] = (acc[s.key] ?? 0) + 1;
        return acc;
    }, {}), [data]);
    // Read counts through a ref: a background refetch's new counts would otherwise flow
    // through addressByOf -> save -> the column memo and remount an open editor.
    const keyCountsRef = useLatestRef(keyCounts);
    const addressByOf = useCallback(
        (s: TSetting): 'key' | 'id' =>
            (repeatableKeySet.has(s.key) || keyCountsRef.current[s.key] > 1 ? 'id' : 'key'),
        [keyCountsRef, repeatableKeySet],
    );

    const cancelEdit = inlineEdit.cancelEdit;

    const startEdit = useCallback((s: TSetting) => {
        draftRef.current = seedConfigDraft(s);
        inlineEdit.beginEdit(s.id);
        // `beginEdit` is stable; the wider `inlineEdit` object churns every render.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inlineEdit.beginEdit]);

    const save = useCallback((s: TSetting) => {
        const setEditError = (error: string | null) => editErrorRef.current?.(error);
        const result = coerceConfigValue(s, draftRef.current);
        if (!result.ok) {
            setEditError(result.error);
            return;
        }

        // Keep the row in edit mode (and pending indicator up) until the server confirms;
        // a rejected save re-surfaces the error and leaves the row open to retry.
        setEditError(null);
        inlineEdit
            .commit(() => (addressByOf(s) === 'id'
                ? mutateAsPromise(updateByIdMutate, { settingId: s.id, value: result.value })
                : mutateAsPromise(updateMutate, { key: s.key, value: result.value })))
            .catch((err) => setEditError(apiErrorMessage(err, 'Failed to save setting')));
        // `commit` is stable; the wider `inlineEdit` object churns every render.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [addressByOf, updateMutate, updateByIdMutate, inlineEdit.commit]);

    const requestDelete = useCallback((s: TSetting) => {
        setDeleteError(null);
        setPendingDelete(s);
    }, []);

    const cancelDelete = () => {
        setPendingDelete(null);
        setDeleteError(null);
    };

    const confirmDelete = () => {
        if (!pendingDelete) return;
        const s = pendingDelete;
        setDeleteError(null);
        const onSuccess = () => setPendingDelete(null);
        const onError = (err: unknown) => setDeleteError(apiErrorMessage(err, 'Failed to delete setting'));
        if (addressByOf(s) === 'id') {
            mutations.deleteByIdMutation.mutate({ settingId: s.id }, { onSuccess, onError });
        } else {
            mutations.deleteMutation.mutate({ key: s.key }, { onSuccess, onError });
        }
    };

    // Stable group sort: with groupOrder an unknown label sorts last (not first);
    // without groupOrder, alphabetical with Uncategorised last.
    const orderIndex = (label: string): number => {
        if (!groupOrder) return 0;
        const idx = groupOrder.indexOf(label);
        return idx === -1 ? groupOrder.length : idx;
    };
    const rows = groupOrder
        ? [...data].sort((a, b) => orderIndex(groupLabel(a)) - orderIndex(groupLabel(b)))
        : [...data].sort((a, b) => {
            const ga = groupLabel(a);
            const gb = groupLabel(b);
            if (ga === gb) return 0;
            if (ga === UNCATEGORISED) return 1;
            if (gb === UNCATEGORISED) return -1;
            return ga.localeCompare(gb);
        });

    const columns = useMemo<ColumnDef<TSetting>[]>(() => [
        {
            id: 'group',
            header: 'Group',
            enableSorting: false,
            cell: ({ row }) => (
                <span className="text-xs uppercase tracking-wide text-mp-muted">
                    {groupLabel(row.original)}
                </span>
            ),
        },
        {
            id: 'setting',
            header: 'Setting',
            enableSorting: false,
            cell: ({ row }) => (
                <span className="font-medium">
                    {row.original.configOption?.label ?? row.original.key}
                </span>
            ),
        },
        {
            id: 'type',
            header: 'Type',
            enableSorting: false,
            cell: ({ row }) => (
                <Badge size="xs" variant="info">{dataTypeOf(row.original)}</Badge>
            ),
        },
        {
            id: 'value',
            header: 'Value',
            enableSorting: false,
            cell: ({ row }) => {
                const s = row.original;
                if (inlineEdit.editingId === s.id) {
                    return <ConfigValueEditCell setting={s} draftRef={draftRef} errorSinkRef={editErrorRef} />;
                }
                return <span className="font-mono text-sm">{formatConfigValue(s)}</span>;
            },
        },
        {
            id: 'actions',
            header: 'Actions',
            enableSorting: false,
            cell: (info) => {
                const { row } = info;
                const s = row.original;
                const label = s.configOption?.label ?? s.key;
                // Retired/view-only entities hide edit/delete so admins cannot trigger mutations the backend rejects.
                if (readOnly) return null;
                if (inlineEdit.editingId === s.id) {
                    // Read isPending via `meta`, not a columns dep: recomputing `columns`
                    // remounts this row's open editor (see the `Table` meta prop).
                    const meta = info.table.options.meta as { isPending?: boolean } | undefined;
                    return (
                        <InlineEditRowActions
                            isPending={!!meta?.isPending}
                            onAccept={() => save(s)}
                            onCancel={cancelEdit}
                            acceptPermission={permissions.edit}
                            acceptLabel={`Save ${label}`}
                            cancelLabel={`Cancel editing ${label}`}
                        />
                    );
                }
                return (
                    <div className="flex gap-2">
                        <PermissionGuard permission={permissions.edit}>
                            <Tooltip content="Edit setting">
                                <TextLink
                                    color="secondary"
                                    ariaLabel={`Edit ${label}`}
                                    startIcon={<SquarePenIcon size={18} />}
                                    onClick={() => startEdit(s)}
                                />
                            </Tooltip>
                        </PermissionGuard>
                        <PermissionGuard permission={permissions.delete}>
                            <Tooltip content="Delete setting">
                                <TextLink
                                    color="error"
                                    ariaLabel={`Delete ${label}`}
                                    startIcon={<Trash2Icon size={18} />}
                                    onClick={() => requestDelete(s)}
                                />
                            </Tooltip>
                        </PermissionGuard>
                    </div>
                );
            },
        },
    ], [inlineEdit.editingId, readOnly, startEdit, save, cancelEdit, requestDelete, permissions, groupLabel]);

    const deleting = mutations.deleteMutation.isPending || mutations.deleteByIdMutation.isPending;
    const pendingLabel = pendingDelete?.configOption?.label ?? pendingDelete?.key ?? '';
    const headingId = `delete-${entityNoun}-config-setting-heading`;

    return (
        <>
            <Table<TSetting>
                data={rows}
                columns={columns}
                enableSorting={false}
                meta={{ isPending: inlineEdit.isPending }}
            />

            <Modal
                isOpen={pendingDelete !== null}
                onClose={cancelDelete}
                ariaLabelledBy={headingId}
                className="max-w-md p-6"
            >
                <h2 id={headingId} className="text-lg font-semibold mb-2">
                    Delete config setting
                </h2>
                <p className="text-sm text-mp-muted mb-4">
                    Delete <span className="font-semibold">{pendingLabel}</span> from this {entityNoun}?
                    This cannot be undone.
                </p>
                {deleteError && (
                    <Alert variant="error" className="mb-4">{deleteError}</Alert>
                )}
                <div className="flex justify-end gap-2">
                    <Button onClick={cancelDelete} variant="clear" disabled={deleting}>
                        Cancel
                    </Button>
                    <Button onClick={confirmDelete} variant="danger" disabled={deleting}>
                        {deleting ? 'Deleting…' : 'Delete'}
                    </Button>
                </div>
            </Modal>
        </>
    );
}
